import type { PeerId, Quality } from '@live-stream/shared'
import {
  ConnectionState as LkConnectionState,
  Room,
  RoomEvent,
  Track,
  VideoPreset,
  VideoQuality,
  type LocalTrack,
  type LocalTrackPublication,
  type LocalVideoTrack,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteVideoTrack,
  type TrackPublishOptions,
} from 'livekit-client'
import { describePath, type ContentHint, type ScreenProfile, type SendProfile } from './encoding.js'
import {
  CAMERA_LAYERS,
  cameraLadderFor,
  CAMERA_TOP,
  connectionStateOf,
  kindOfSource,
  layerFor,
  MICROPHONE_BITRATE,
  SCREEN_AUDIO_BITRATE,
  screenEncodingFor,
} from './sfu-policy.js'
import type {
  ConnectionState,
  MediaTransport,
  PeerStats,
  PublishedTrack,
  PublishOptions,
  TrackKind,
  TransportEvent,
  TransportEventMap,
  TransportHandler,
  TransportStats,
  Unsubscribe,
} from './transport.js'

/**
 * The SFU transport (Phase 7): LiveKit behind the same MediaTransport the
 * mesh implements, so nothing above this package changes.
 *
 * Every participant uploads ONE copy of each track to the server, in three
 * spatial layers for the camera, and the server forwards to each
 * subscriber the layer their tile can use — which is what lets a room grow
 * past four without every laptop encoding for everyone. Screen shares are
 * one layer; the SFU pauses subscribers who cannot afford it. Identity is
 * our peer id: the token our API mints names it, so a remote track's
 * participant IS the peer the room knows.
 *
 * What the mesh had to work out for itself the SFU simply says: which
 * source a track is (camera, microphone, screen, screen audio) rides on
 * its publication, so no mid map is needed; the room's connection state
 * stands for every link; the server picks layers when asked and drops the
 * ones nobody wants (dynacast).
 */

export interface SfuTransportOptions {
  localPeerId: PeerId
  /** wss://… — the server's LiveKit address, from the room's pre-join info. */
  url: string
  /** Send the camera as VP9 (see cameraCodecFor); VP8 otherwise. */
  cameraCodec?: 'vp9' | 'vp8'
}

type Handlers = {
  [E in TransportEvent]: Set<TransportHandler<E>>
}

interface PeerStreams {
  media: MediaStream
  screen: MediaStream
}

const isScreenKind = (kind: TrackKind): boolean => kind === 'screen' || kind === 'screen-audio'

export class SfuTransport implements MediaTransport {
  readonly name = 'sfu' as const

  private readonly room: Room
  private readonly localTracks = new Map<TrackKind, MediaStreamTrack>()
  private readonly publications = new Map<TrackKind, LocalTrackPublication>()
  /** Tracks we asked to publish before the room was connected; published on connect. */
  private readonly pending = new Map<TrackKind, MediaStreamTrack>()
  /** One camera-and-microphone stream and one screen stream per peer, the way the mesh assembles them. */
  private readonly streams = new Map<PeerId, PeerStreams>()
  /** The layer each peer's camera should arrive in, from the layout engine. */
  private readonly wanted = new Map<PeerId, Quality>()
  private screenHint: ContentHint = 'detail'
  private roomState: ConnectionState = 'new'
  private closed = false

  private readonly handlers: Handlers = {
    'track-added': new Set(),
    'track-removed': new Set(),
    'peer-joined': new Set(),
    'peer-left': new Set(),
    'connection-state': new Set(),
    stats: new Set(),
    error: new Set(),
  }

  constructor(private readonly opts: SfuTransportOptions) {
    this.room = new Room({
      // The layout engine decides what each tile receives and says so
      // through setSubscribedQuality; the SDK's own element-size guessing
      // would fight it. Dynacast stays on: a layer nobody wants is not encoded.
      adaptiveStream: false,
      dynacast: true,
      disconnectOnPageLeave: true,
      // Our tracks are ours: the camera and mic stop when the person turns
      // them off or leaves (unpublishKind and close say so), not because the
      // SFU let the connection go — they are sent again on the way back.
      stopLocalTrackOnUnpublish: false,
      publishDefaults: {
        simulcast: true,
        dtx: true,
        red: true,
        videoCodec: 'vp8',
        backupCodec: true,
      },
    })
    this.wire()
  }

  private wire(): void {
    this.room
      .on(RoomEvent.TrackSubscribed, (track, publication, participant) =>
        this.onSubscribed(track, publication, participant),
      )
      .on(RoomEvent.TrackUnsubscribed, (track, publication, participant) =>
        this.onUnsubscribed(track, publication, participant),
      )
      .on(RoomEvent.ParticipantConnected, (participant) => {
        this.emit('peer-joined', { peerId: participant.identity })
        this.emit('connection-state', { peerId: participant.identity, state: 'connected' })
      })
      .on(RoomEvent.ParticipantDisconnected, (participant) => this.forget(participant.identity))
      .on(RoomEvent.ConnectionStateChanged, (state) => this.onRoomState(state))
      .on(RoomEvent.Disconnected, () => {
        // Dropped by the SFU (removed, replaced, restarted), not closed by
        // us: what we were sending went with the connection. Keep it ready
        // to send again when the room is joined again (connect()).
        if (this.closed) return
        for (const [kind, track] of this.localTracks) {
          if (track.readyState === 'live') this.pending.set(kind, track)
        }
        this.publications.clear()
      })
      .on(RoomEvent.MediaDevicesError, (err) => {
        this.emit('error', { code: 'media_devices', message: String(err) })
      })
  }

  private streamsFor(peerId: PeerId): PeerStreams {
    let streams = this.streams.get(peerId)
    if (!streams) {
      streams = { media: new MediaStream(), screen: new MediaStream() }
      this.streams.set(peerId, streams)
    }
    return streams
  }

  private onSubscribed(
    track: RemoteTrack,
    publication: RemoteTrackPublication,
    participant: RemoteParticipant,
  ): void {
    const peerId = participant.identity
    const kind =
      kindOfSource(publication.source) ??
      (track.kind === Track.Kind.Audio ? 'microphone' : 'camera')
    const streams = this.streamsFor(peerId)
    const stream = isScreenKind(kind) ? streams.screen : streams.media
    const mediaTrack = track.mediaStreamTrack
    if (!stream.getTrackById(mediaTrack.id)) stream.addTrack(mediaTrack)
    if (kind === 'camera') this.applyQuality(peerId)
    this.emit('track-added', { id: mediaTrack.id, peerId, kind, track: mediaTrack, stream })
  }

  private onUnsubscribed(
    track: RemoteTrack,
    publication: RemoteTrackPublication,
    participant: RemoteParticipant,
  ): void {
    const peerId = participant.identity
    const kind =
      kindOfSource(publication.source) ??
      (track.kind === Track.Kind.Audio ? 'microphone' : 'camera')
    const streams = this.streams.get(peerId)
    const mediaTrack = track.mediaStreamTrack
    if (streams) (isScreenKind(kind) ? streams.screen : streams.media).removeTrack(mediaTrack)
    this.emit('track-removed', { id: mediaTrack.id, peerId, kind })
  }

  private forget(peerId: PeerId): void {
    const streams = this.streams.get(peerId)
    if (streams) {
      for (const s of [streams.media, streams.screen])
        for (const t of s.getTracks()) s.removeTrack(t)
    }
    this.streams.delete(peerId)
    this.wanted.delete(peerId)
    this.emit('peer-left', { peerId })
  }

  /**
   * One connection stands for every link. When the room is reconnecting,
   * every peer is; when it is back, every peer is.
   */
  private onRoomState(state: LkConnectionState): void {
    const ours = connectionStateOf(state)
    this.roomState = ours
    this.emit('connection-state', { peerId: null, state: ours })
    for (const participant of this.room.remoteParticipants.values()) {
      this.emit('connection-state', { peerId: participant.identity, state: ours })
    }
    if (state === LkConnectionState.Connected) void this.publishPending()
  }

  // ---------------------------------------------------------------- lifecycle

  /**
   * Join the LiveKit room with the token our API minted. Idempotent: a
   * second call while connected returns at once; after a disconnect (a
   * token past its time, an outage longer than the SDK's own retries) it
   * connects again with the token it is given — the caller has a fresh one
   * from the rejoin.
   */
  async connect(_roomId: string, token: string): Promise<void> {
    if (this.closed) return
    if (this.room.state === LkConnectionState.Connected) return
    await this.room.connect(this.opts.url, token, { autoSubscribe: true })
    this.roomState = 'connected'
    // Whoever was already here: announce them the way the mesh announces
    // a peer:joined, and their tracks follow as subscriptions.
    for (const participant of this.room.remoteParticipants.values()) {
      this.emit('peer-joined', { peerId: participant.identity })
      this.emit('connection-state', { peerId: participant.identity, state: 'connected' })
    }
    await this.publishPending()
  }

  private async publishPending(): Promise<void> {
    for (const [kind, track] of [...this.pending]) {
      this.pending.delete(kind)
      await this.doPublish(kind, track)
    }
  }

  // ---------------------------------------------------------------- publishing

  private publishOptions(kind: TrackKind, track: MediaStreamTrack): TrackPublishOptions {
    switch (kind) {
      case 'camera': {
        const settings = track.getSettings()
        const ladder = cameraLadderFor(
          Math.max(settings.width ?? 0, settings.height ?? 0) || undefined,
        )
        if (this.opts.cameraCodec === 'vp9') {
          // One VP9 stream with its three sizes inside (SVC): each viewer is
          // still sent the size their tile needs, for less data than VP8's
          // three. A viewer who cannot play VP9 gets a VP8 copy (backupCodec).
          return {
            source: Track.Source.Camera,
            name: 'camera',
            videoCodec: 'vp9',
            backupCodec: true,
            scalabilityMode: 'L3T3_KEY',
            videoEncoding: {
              maxBitrate: ladder.topVp9Bitrate,
              maxFramerate: CAMERA_TOP.maxFramerate,
            },
            degradationPreference: 'balanced',
          }
        }
        const q = CAMERA_LAYERS[0]
        const h = ladder.mid
        return {
          source: Track.Source.Camera,
          name: 'camera',
          simulcast: true,
          videoCodec: 'vp8',
          videoEncoding: {
            maxBitrate: ladder.topBitrate,
            maxFramerate: CAMERA_TOP.maxFramerate,
          },
          videoSimulcastLayers: [q, h].flatMap((l) =>
            l ? [new VideoPreset(l.width, l.height, l.maxBitrate, l.maxFramerate)] : [],
          ),
          degradationPreference: 'balanced',
        }
      }
      case 'microphone':
        return {
          source: Track.Source.Microphone,
          name: 'microphone',
          dtx: true,
          red: true,
          audioPreset: { maxBitrate: MICROPHONE_BITRATE },
        }
      case 'screen': {
        const encoding = screenEncodingFor(this.screenHint)
        return {
          source: Track.Source.ScreenShare,
          name: 'screen',
          // One layer: the SFU pauses subscribers who cannot afford it rather
          // than us encoding three copies of a slide. VP9 for its
          // screen-content tools, with a VP8 backup for a peer without it.
          simulcast: false,
          videoCodec: 'vp9',
          backupCodec: true,
          screenShareEncoding: {
            maxBitrate: encoding.maxBitrate,
            maxFramerate: encoding.maxFramerate,
          },
          degradationPreference: encoding.degradationPreference,
        }
      }
      case 'screen-audio':
        return {
          source: Track.Source.ScreenShareAudio,
          name: 'screen-audio',
          dtx: false,
          red: true,
          audioPreset: { maxBitrate: SCREEN_AUDIO_BITRATE },
        }
    }
  }

  private async doPublish(kind: TrackKind, track: MediaStreamTrack, attempt = 0): Promise<void> {
    if (this.closed || track.readyState === 'ended') return
    // Publishing switches a track on; ours may be off on purpose (a muted
    // mic is a disabled track). Whatever it was, it stays — or a muted mic
    // would send sound, sent again after a reconnect, say, while every
    // screen shows it muted.
    const enabled = track.enabled
    try {
      const publication = await this.room.localParticipant.publishTrack(
        track,
        this.publishOptions(kind, track),
      )
      track.enabled = enabled
      this.publications.set(kind, publication)
    } catch (err) {
      // Just given the right to send it (a host let us speak), the SFU may
      // hear so a moment after we do: try again, briefly, before saying so.
      if (/permission/i.test(String(err)) && attempt < 3 && !this.closed) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)))
        return this.doPublish(kind, track, attempt + 1)
      }
      this.emit('error', { code: 'publish_failed', message: `${kind}: ${String(err)}` })
    }
  }

  async publish(track: MediaStreamTrack, opts: PublishOptions): Promise<PublishedTrack> {
    if (opts.contentHint) track.contentHint = opts.contentHint
    if (opts.kind === 'screen')
      this.screenHint = opts.contentHint === 'motion' ? 'motion' : 'detail'
    this.localTracks.set(opts.kind, track)

    if (this.room.state === LkConnectionState.Connected) await this.doPublish(opts.kind, track)
    else this.pending.set(opts.kind, track)

    const kind = opts.kind
    return {
      id: track.id,
      kind,
      track,
      replace: async (next) => {
        const publication = this.publications.get(kind)
        if (next) {
          this.localTracks.set(kind, next)
          const local = publication?.track
          if (local) {
            // A device switch or a camera coming back: the same publication,
            // a new source. No renegotiation, and the subscribers notice
            // nothing but the picture.
            await local.replaceTrack(next, true)
            if (publication.isMuted) await publication.unmute()
          } else if (this.room.state === LkConnectionState.Connected) {
            await this.doPublish(kind, next)
          } else {
            this.pending.set(kind, next)
          }
        } else {
          // Camera off: the publication stays, muted, so the subscribers
          // keep their slot and the return is a replace, not a renegotiation.
          this.localTracks.delete(kind)
          this.pending.delete(kind)
          if (publication && !publication.isMuted) await publication.mute()
        }
      },
      setContentHint: (hint) => {
        track.contentHint = hint
        if (kind !== 'screen') return
        this.screenHint = hint === 'motion' ? 'motion' : 'detail'
        const local = this.publications.get(kind)?.track as LocalVideoTrack | undefined
        void local?.setDegradationPreference(
          screenEncodingFor(this.screenHint).degradationPreference,
        )
      },
      stop: () => {
        track.stop()
        this.localTracks.delete(kind)
        this.pending.delete(kind)
        void this.unpublishKind(kind)
      },
    }
  }

  private async unpublishKind(kind: TrackKind): Promise<void> {
    const publication = this.publications.get(kind)
    this.publications.delete(kind)
    const local: LocalTrack | undefined = publication?.track
    if (!local) return
    try {
      await this.room.localParticipant.unpublishTrack(local, true)
    } catch (err) {
      this.emit('error', { code: 'unpublish_failed', message: `${kind}: ${String(err)}` })
    }
  }

  async unpublish(trackId: string): Promise<void> {
    for (const [kind, track] of this.localTracks) {
      if (track.id !== trackId) continue
      this.localTracks.delete(kind)
      this.pending.delete(kind)
      await this.unpublishKind(kind)
      return
    }
  }

  // ---------------------------------------------------------------- subscribing

  /**
   * The layout engine's verdict for one tile: which layer to receive, or
   * none. 'off' pauses the subscription — the server stops forwarding, the
   * publisher may stop encoding that layer — and anything else resumes it
   * at the layer asked for. This is the single biggest lever on CPU and
   * bandwidth in a large room.
   */
  setSubscribedQuality(peerId: PeerId, _trackId: string, quality: Quality): void {
    this.wanted.set(peerId, quality)
    this.applyQuality(peerId)
  }

  private applyQuality(peerId: PeerId): void {
    const participant = this.room.remoteParticipants.get(peerId)
    const publication = participant?.getTrackPublication(Track.Source.Camera) as
      RemoteTrackPublication | undefined
    if (!publication) return
    const choice = layerFor(this.wanted.get(peerId) ?? 'high')
    if (choice === 'off') {
      publication.setEnabled(false)
      return
    }
    publication.setEnabled(true)
    publication.setVideoQuality(
      choice === 'low'
        ? VideoQuality.LOW
        : choice === 'medium'
          ? VideoQuality.MEDIUM
          : VideoQuality.HIGH,
    )
  }

  // ---------------------------------------------------------------- events

  on<E extends TransportEvent>(event: E, handler: TransportHandler<E>): Unsubscribe {
    this.handlers[event].add(handler as never)
    return () => {
      this.handlers[event].delete(handler as never)
    }
  }

  private emit<E extends TransportEvent>(event: E, payload: TransportEventMap[E]): void {
    for (const handler of this.handlers[event]) {
      ;(handler as TransportHandler<E>)(payload)
    }
  }

  // ---------------------------------------------------------------- stats

  /**
   * Per peer, what arrives from them; once, what leaves us — an SFU has one
   * uplink, so our encoder's numbers are the same toward everyone and are
   * repeated on every row.
   */
  async getStats(): Promise<TransportStats> {
    const camera = this.publications.get('camera')?.track as LocalVideoTrack | undefined
    const screen = this.publications.get('screen')?.track as LocalVideoTrack | undefined

    // Our camera's top layer, and how the packets travel.
    let sentWidth: number | null = null
    let sentHeight: number | null = null
    let sentFps: number | null = null
    let rtt: number | null = null
    let limitation: TransportStats['qualityLimitationReason'] = 'none'
    let path: PeerStats['path'] = 'unknown'
    let sendLayers: string | undefined
    if (camera) {
      const layers = await camera.getSenderStats().catch(() => [])
      sendLayers = [...layers]
        .sort((a, b) => a.frameWidth - b.frameWidth)
        .map(
          (l) =>
            `${l.rid || '-'} ${l.frameWidth}×${l.frameHeight}@${Math.round(l.framesPerSecond)} → ${Math.round((l.targetBitrate ?? 0) / 1000)}k${
              l.qualityLimitationReason && l.qualityLimitationReason !== 'none'
                ? ` (${l.qualityLimitationReason})`
                : ''
            }`,
        )
        .join(' · ')
      const top = layers.reduce<(typeof layers)[number] | null>(
        (best, l) => (!best || l.frameWidth > best.frameWidth ? l : best),
        null,
      )
      if (top) {
        sentWidth = top.frameWidth || null
        sentHeight = top.frameHeight || null
        sentFps = top.framesPerSecond || null
        if (typeof top.roundTripTime === 'number') rtt = Math.round(top.roundTripTime * 1000)
        const reason = top.qualityLimitationReason
        if (reason === 'cpu' || reason === 'bandwidth' || reason === 'other') limitation = reason
      }
      path = await pathOf(camera)
    }

    // Our share, once.
    let screenSent: Pick<
      PeerStats,
      | 'screenSentWidth'
      | 'screenSentHeight'
      | 'screenSentFramesPerSecond'
      | 'screenBytesSent'
      | 'screenFramesEncoded'
      | 'screenLimitation'
    > = {
      screenSentWidth: null,
      screenSentHeight: null,
      screenSentFramesPerSecond: null,
      screenBytesSent: null,
      screenFramesEncoded: null,
      screenLimitation: 'none',
    }
    if (screen) {
      const [s] = await screen.getSenderStats().catch(() => [])
      if (s) {
        const reason = s.qualityLimitationReason
        screenSent = {
          screenSentWidth: s.frameWidth || null,
          screenSentHeight: s.frameHeight || null,
          screenSentFramesPerSecond: s.framesPerSecond || null,
          screenBytesSent: s.bytesSent ?? null,
          screenFramesEncoded: s.framesSent ?? null,
          screenLimitation:
            reason === 'cpu' || reason === 'bandwidth' || reason === 'other' ? reason : 'none',
        }
        if (path === 'unknown') path = await pathOf(screen)
      }
    }

    const perPeer: TransportStats['perPeer'] = {}
    let lossSum = 0
    for (const participant of this.room.remoteParticipants.values()) {
      const video = participant.getTrackPublication(Track.Source.Camera)?.track as
        RemoteVideoTrack | undefined
      const audio = participant.getTrackPublication(Track.Source.Microphone)?.track as
        RemoteAudioTrack | undefined
      const v = video ? await video.getReceiverStats().catch(() => undefined) : undefined
      const a = audio ? await audio.getReceiverStats().catch(() => undefined) : undefined
      const received = (v?.packetsReceived ?? 0) + (a?.packetsReceived ?? 0)
      const lost = (v?.packetsLost ?? 0) + (a?.packetsLost ?? 0)
      const total = received + lost
      const ratio = total > 0 ? lost / total : 0
      lossSum += ratio
      perPeer[participant.identity] = {
        peerId: participant.identity,
        connectionState: this.roomState,
        roundTripTimeMs: rtt,
        packetsReceived: received,
        packetsLost: lost,
        packetsLostRatio: ratio,
        jitterMs: Math.round((v?.jitter ?? a?.jitter ?? 0) * 1000),
        framesPerSecond: null,
        frameWidth: v?.frameWidth ?? null,
        frameHeight: v?.frameHeight ?? null,
        receivedCodec: v
          ? [v.mimeType?.replace('video/', ''), v.decoderImplementation]
              .filter(Boolean)
              .join(' · ') || null
          : null,
        freezeCount: 0,
        qualityLimitationReason: limitation,
        sentWidth,
        sentHeight,
        sentFramesPerSecond: sentFps,
        ...screenSent,
        screenEncodeTimeSeconds: null,
        screenCodec: screen ? 'video/VP9' : null,
        screenScale: screen ? 1 : null,
        path,
      }
    }

    const peers = Object.keys(perPeer).length
    const sendProfile: SendProfile | null = camera
      ? (() => {
          const s = camera.mediaStreamTrack.getSettings()
          const ladder = cameraLadderFor(Math.max(s.width ?? 0, s.height ?? 0) || undefined)
          return {
            width: s.width ?? CAMERA_TOP.width,
            height: s.height ?? CAMERA_TOP.height,
            maxFramerate: CAMERA_TOP.maxFramerate,
            maxBitrate: ladder.topBitrate,
          }
        })()
      : null
    const screenProfile: ScreenProfile | null = screen
      ? { ...screenEncodingFor(this.screenHint), scaleResolutionDownBy: 1 }
      : null

    return {
      outboundBitrate: 0,
      inboundBitrate: 0,
      roundTripTimeMs: rtt,
      packetsLostRatio: peers > 0 ? lossSum / peers : 0,
      qualityLimitationReason: limitation,
      sendProfile,
      screenProfile,
      ...(sendLayers !== undefined ? { sendLayers } : {}),
      perPeer,
    }
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    void this.room.disconnect()
    this.localTracks.clear()
    this.pending.clear()
    this.publications.clear()
    this.streams.clear()
    this.wanted.clear()
    for (const set of Object.values(this.handlers)) set.clear()
  }
}

/** How a local track's packets travel, from its sender's nominated candidate pair. */
async function pathOf(track: LocalTrack): Promise<PeerStats['path']> {
  const report = await track.getRTCStatsReport().catch(() => undefined)
  if (!report) return 'unknown'
  const byId = new Map<string, Record<string, unknown>>()
  report.forEach((s) => byId.set(String((s as { id: string }).id), s as Record<string, unknown>))
  let path: PeerStats['path'] = 'unknown'
  report.forEach((s) => {
    if (s.type === 'candidate-pair' && s.state === 'succeeded' && s.nominated) {
      path = describePath(
        byId.get(String(s.localCandidateId)) as never,
        byId.get(String(s.remoteCandidateId)) as never,
      )
    }
  })
  return path
}
